import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  OverlayDrawer,
  Tab,
  TabList,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { X } from 'lucide-react';

import { AddDevicesResponseData, MigrationType, addDevices } from '../api/migrations';
import { ApiError } from '../api/client';
import BulkDeviceActionDialog, { parseSerialNumbers } from './BulkDeviceActionDialog';
import BulkActionResultView, {
  DeviceActionResult,
  DeviceActionResultRow,
} from './BulkActionResultView';
import SourceDeviceLookup from './SourceDeviceLookup';
import SourceGroupPicker from './SourceGroupPicker';
import { IDENTIFIER } from '../theme/tokens';

export interface AssignDevicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migrationId: string;
  // The migration's type. Only `T2T` unlocks the source-tenant serial lookup
  // (there is no source tenant for D2C). When omitted or non-T2T, the dialog is
  // the plain paste-serials flow — the universal fallback (FD-112).
  migrationType?: MigrationType;
  // Called when the server returns at least one successfully added device so the
  // parent can invalidate the migration query and refresh the device grid.
  onDevicesAssigned?: () => void;
}

export default function AssignDevicesDialog({
  open,
  onOpenChange,
  migrationId,
  migrationType,
  onDevicesAssigned,
}: AssignDevicesDialogProps) {
  // T2T gains the source-tenant lookup tab (FD-112). Every other migration type
  // keeps the plain paste-serials dialog unchanged — the lookup mode is only
  // meaningful when a source tenant exists.
  if (migrationType === 'T2T') {
    return (
      <AssignDevicesT2TDialog
        open={open}
        onOpenChange={onOpenChange}
        migrationId={migrationId}
        onDevicesAssigned={onDevicesAssigned}
      />
    );
  }

  return (
    <BulkDeviceActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Assign devices"
      description="Paste device serial numbers — one per line or comma-separated. Each serial can only belong to one migration at a time."
      submitLabel={(count) => `Assign ${count} device${count === 1 ? '' : 's'}`}
      submittingLabel="Assigning…"
      resetLabel="Assign more"
      inputPlaceholder={'SN-0001\nSN-0002\nSN-0003'}
      onSubmit={async (serials) => {
        const result = await addDevices(migrationId, serials);
        return normalizeAddResult(result.data, result.message);
      }}
      onProcessed={(result) => {
        const addedCount = result.rows.filter((r) => r.intent === 'success').length;
        if (addedCount > 0) onDevicesAssigned?.();
      }}
    />
  );
}

const useStyles = makeStyles({
  // Wide right-anchored blade. Generous width so the full device grid (7 columns
  // + selection) reads without horizontal scroll on operator-width displays;
  // clamps to the viewport on smaller screens. Body scrolls vertically for long
  // device lists.
  drawer: {
    width: 'min(1120px, 96vw)',
    maxWidth: '96vw',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
  },
  description: {
    fontSize: '13px',
    color: '#52525b',
  },
  countLine: {
    fontSize: '12px',
    color: '#52525b',
  },
  textarea: {
    ...IDENTIFIER,
    minHeight: '140px',
  },
  // Footer actions pinned at the drawer bottom, right-aligned.
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    columnGap: '8px',
  },
});

// T2T assign dialog: three input methods behind a TabList — "Select group"
// (FD-113 source-tenant group targeting, the operator's PREFERRED path and the
// default tab), "Look up source device" (FD-112 exact serial search), and
// "Paste serials" (the unchanged fallback) — all contributing serials to ONE
// staged set. A single footer submit assigns the union via the unchanged
// serial-keyed addDevices call, and renders the shared BulkActionResultView.
// The group and lookup tabs both select real source-tenant devices, so they
// share a single `stagedSerials` set; the paste tab parses its own textarea.
// Keeping one staged set + one submit avoids forking add-devices per tab.
function AssignDevicesT2TDialog({
  open,
  onOpenChange,
  migrationId,
  onDevicesAssigned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migrationId: string;
  onDevicesAssigned?: () => void;
}) {
  const styles = useStyles();
  const [tab, setTab] = useState<'group' | 'lookup' | 'paste'>('group');
  // Shared by the group + lookup tabs — both stage REAL source-tenant device
  // serials. The paste tab parses its own textarea. This is the "ONE unified
  // staged set" the footer submits.
  const [stagedSerials, setStagedSerials] = useState<Set<string>>(new Set());
  const [pasteInput, setPasteInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<DeviceActionResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Reset everything on dismiss so a re-open starts fresh.
  useEffect(() => {
    if (!open) {
      setTab('group');
      setStagedSerials(new Set());
      setPasteInput('');
      setIsSubmitting(false);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const pasteSerials = useMemo(() => parseSerialNumbers(pasteInput), [pasteInput]);

  // Union of all input methods, deduped, insertion order preserved (group /
  // looked-up serials first, then any additional pasted serials).
  const serials = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of [...stagedSerials, ...pasteSerials]) {
      if (seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }, [stagedSerials, pasteSerials]);

  // Memoized + functional-update so the group tab can safely default-stage a
  // whole page of resolved serials in one effect pass (N sequential toggles
  // compose correctly) without re-triggering the picker's auto-stage effect.
  const toggleStagedSerial = useCallback((serial: string) => {
    setStagedSerials((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });
  }, []);

  const handleSubmit = async () => {
    if (serials.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await addDevices(migrationId, serials);
      const next = normalizeAddResult(res.data, res.message);
      setResult(next);
      const addedCount = next.rows.filter((r) => r.intent === 'success').length;
      if (addedCount > 0) onDevicesAssigned?.();
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setStagedSerials(new Set());
    setPasteInput('');
    setResult(null);
    setError(null);
    setTab('group');
  };

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      position="end"
      className={styles.drawer}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<X size={20} strokeWidth={1.5} />}
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            />
          }
        >
          Assign devices
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {result ? (
          <BulkActionResultView result={result} resetLabel="Assign more" />
        ) : (
          <div className={styles.body}>
            {error ? <ErrorView error={error} /> : null}
            <TabList
              selectedValue={tab}
              onTabSelect={(_, data) =>
                setTab(data.value as 'group' | 'lookup' | 'paste')
              }
            >
              <Tab value="group">Select group</Tab>
              <Tab value="lookup">Look up source device</Tab>
              <Tab value="paste">Paste serials</Tab>
            </TabList>

            {tab === 'group' ? (
              <SourceGroupPicker
                migrationId={migrationId}
                selectedSerials={stagedSerials}
                onToggleSerial={toggleStagedSerial}
                disabled={isSubmitting}
              />
            ) : tab === 'lookup' ? (
              <SourceDeviceLookup
                migrationId={migrationId}
                selectedSerials={stagedSerials}
                onToggleSerial={toggleStagedSerial}
                disabled={isSubmitting}
              />
            ) : (
              <>
                <div className={styles.description}>
                  Paste device serial numbers — one per line or comma-separated. Each
                  serial can only belong to one migration at a time.
                </div>
                <Field>
                  <Textarea
                    value={pasteInput}
                    onChange={(_, data) => setPasteInput(data.value)}
                    placeholder={'SN-0001\nSN-0002\nSN-0003'}
                    disabled={isSubmitting}
                    className={styles.textarea}
                    resize="vertical"
                  />
                </Field>
              </>
            )}

            <div className={styles.countLine}>
              {serials.length === 0
                ? 'Select a group, look up a device, or paste serial numbers to stage them for assignment.'
                : `${serials.length} unique device${serials.length === 1 ? '' : 's'} staged for assignment.`}
            </div>
          </div>
        )}
      </DrawerBody>
      <DrawerFooter>
        <div className={styles.footerActions}>
          {result ? (
            <>
              <Button appearance="secondary" onClick={handleReset}>
                Assign more
              </Button>
              <Button appearance="primary" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                appearance="subtle"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={serials.length === 0 || isSubmitting}
              >
                {isSubmitting
                  ? 'Assigning…'
                  : `Assign ${serials.length} device${serials.length === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
}

function ErrorView({ error }: { error: unknown }) {
  const title =
    error instanceof ApiError ? `Request failed (${error.status})` : 'Unexpected error';
  const body =
    error instanceof Error ? error.message : 'An unknown error occurred. Please try again.';
  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {body}
      </MessageBarBody>
    </MessageBar>
  );
}

// Maps the add-devices response buckets to the shared result-view rows. Shared
// by the plain paste flow and the T2T tabbed flow so both render identically.
export function normalizeAddResult(
  data: AddDevicesResponseData,
  message: string | null,
): DeviceActionResult {
  const rows: DeviceActionResultRow[] = [];

  for (const serial of data.added ?? []) {
    rows.push({ serialNumber: serial, result: 'Assigned', detail: null, intent: 'success' });
  }
  for (const r of data.rejected ?? []) {
    rows.push({
      serialNumber: r.serialNumber,
      result: 'Rejected',
      detail: r.reason,
      intent: 'warning',
    });
  }
  for (const r of data.indexFailures ?? []) {
    rows.push({
      serialNumber: r.serialNumber,
      result: 'Index failure',
      detail: r.reason,
      intent: 'danger',
    });
  }
  // MINT-failed devices also appear in `added` (they stay assigned to the
  // migration) but their DeviceIndex entry was rolled back, so they cannot
  // boot until re-registered (FD-ADR-016). Flag them alongside their
  // Assigned row.
  for (const r of data.mintFailures ?? []) {
    rows.push({
      serialNumber: r.serialNumber,
      result: 'MINT registration failed',
      detail: r.reason,
      intent: 'danger',
    });
  }

  return {
    rows,
    summaryMessage:
      message ??
      `${data.addedCount} assigned${data.rejectedCount > 0 ? `, ${data.rejectedCount} rejected` : ''}.`,
  };
}
